import { ArrowLeft, BarChart3, Clock3, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NewsEditorialCard } from "@/components/NewsEditorialCard";
import { NewsReadingProgressBar } from "@/components/NewsReadingProgressBar";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { useStructuredData } from "@/hooks/useStructuredData";
import { api } from "@/lib/api";
import { formatDate, formatListingPrice } from "@/lib/portalHelpers";

export default function NewsArticlePage() {
  const { articleId } = useParams();
  const { buildBrandedPath } = useSiteBranding();
  const [article, setArticle] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);

  useSeo({
    title: article ? `${article.title} | SG Prop Portal Insights` : "Market Insight | SG Prop Portal",
    description: article?.excerpt || "A premium SG Prop Portal market insight with related reading and matching opportunities.",
  });

  const loadArticle = useCallback(async () => {
    try {
      const response = await api.get(`/insights/${articleId}`);
      setArticle(response.data);
    } catch {
      setArticle(null);
    }
  }, [articleId]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setReadingProgress(nextProgress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const leadingParagraphs = useMemo(() => article?.paragraphs?.slice(0, 2) || [], [article]);
  const trailingParagraphs = useMemo(() => article?.paragraphs?.slice(2) || [], [article]);
  const authorInitials = useMemo(() => {
    const authorName = article?.author_name || "SG Prop Portal";
    if (authorName.toLowerCase().includes("sg prop portal")) {
      return "SG";
    }
    return authorName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "SG";
  }, [article]);

  const articleStructuredData = useMemo(() => {
    if (!article) return null;
    const pageUrl = `https://sgpropportal.com/insights/${article.slug || article.id}`;
    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      description: article.excerpt,
      image: article.image_url ? [article.image_url] : undefined,
      datePublished: article.published_at,
      dateModified: article.updated_at || article.published_at,
      author: {
        "@type": "Person",
        name: article.author_name || "SG Prop Portal Editorial Desk",
      },
      publisher: {
        "@type": "Organization",
        name: "SG Prop Portal",
        logo: {
          "@type": "ImageObject",
          url: "https://sgpropportal.com/icons/sg-prop-portal-gold-512.png",
        },
      },
      mainEntityOfPage: pageUrl,
      articleSection: article.category || "Market Insights",
      keywords: [article.market_focus, article.category, "Singapore property", "market insights"].filter(Boolean).join(", "),
    };
  }, [article]);

  const articleBreadcrumbSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://sgpropportal.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Insights",
          item: "https://sgpropportal.com/insights",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `https://sgpropportal.com/insights/${article.slug || article.id}`,
        },
      ],
    };
  }, [article]);

  useStructuredData("news-article-schema", articleStructuredData);
  useStructuredData("news-article-breadcrumb", articleBreadcrumbSchema);

  if (!article) {
    return (
      <section className="section-shell">
        <div className="rounded-[28px] border border-[#001935]/10 bg-white/85 p-8 text-[#333333]" data-testid="news-article-loading-state">
          Loading editorial...
        </div>
      </section>
    );
  }

  return (
    <div className="pb-24" data-testid="news-article-page">
      <NewsReadingProgressBar progress={readingProgress} />

      <section className="relative overflow-hidden bg-[#001935]" data-testid="news-article-hero">
        <img alt={article.title} className="absolute inset-0 h-full w-full object-cover" src={article.image_url} />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,25,53,0.16)_0%,rgba(0,25,53,0.32)_32%,rgba(0,25,53,0.88)_100%)]" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 lg:px-12 lg:py-28">
          <Link className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#F5D98C]" data-testid="news-article-back-link" to={buildBrandedPath("/insights")}>
            <ArrowLeft className="h-4 w-4" /> Back to Insights Hub
          </Link>
          <div className="mt-16 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#F5D98C]" data-testid="news-article-eyebrow">{article.eyebrow || article.category}</p>
            <h1 className="mt-5 text-5xl font-semibold leading-[0.95] text-white md:text-6xl" data-testid="news-article-title">{article.title}</h1>
            <p className="mt-6 max-w-3xl text-base leading-8 text-white/80 md:text-lg">{article.hero_caption || article.excerpt}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-white/78" data-testid="news-article-meta">
              <span>{formatDate(article.published_at)}</span>
              <span className="inline-flex items-center gap-2"><Clock3 className="h-4 w-4 text-[#D4AF37]" /> {article.read_time}</span>
              {article.market_focus ? <span className="rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#F5D98C]">{article.market_focus}</span> : null}
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell pt-12">
        <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_320px]">
          <article className="rounded-[34px] border border-[#001935]/10 bg-white/88 p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-10" data-testid="news-article-content-card">
            <div className="rounded-[28px] border border-[#D4AF37]/25 bg-[#fffaf1] px-6 py-6" data-testid="news-article-lead-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Lead Summary</p>
              <p className="mt-4 text-xl leading-9 text-[#31455f]">{article.excerpt}</p>
            </div>

            <div className="mt-10 space-y-7 text-base leading-8 text-[#31455f] md:text-lg" data-testid="news-article-body">
              {leadingParagraphs.map((paragraph, index) => (
                <p key={`lead-${index}`}>{paragraph}</p>
              ))}
            </div>

            {article.pull_quote ? (
              <blockquote className="my-12 border-l-2 border-[#D4AF37] pl-6 text-3xl font-semibold italic leading-tight text-[#001935]" data-testid="news-article-pull-quote">
                “{article.pull_quote}”
              </blockquote>
            ) : null}

            <div className="space-y-7 text-base leading-8 text-[#31455f] md:text-lg">
              {trailingParagraphs.map((paragraph, index) => (
                <p key={`trail-${index}`}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-12 rounded-[28px] border border-[#001935]/10 bg-[#F3EFE7] px-6 py-6" data-testid="news-article-byline-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Editorial Desk</p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#001935] text-lg font-semibold text-[#D4AF37]">{authorInitials}</div>
                <div>
                  <p className="text-xl font-semibold text-[#001935]">{article.author_name}</p>
                  <p className="text-sm text-[#42566f]">{article.author_role}</p>
                </div>
              </div>
              <p className="mt-5 text-sm leading-7 text-[#42566f]">Each editorial is designed to turn broad market headlines into clearer shortlist, pricing, and negotiation decisions for clients in Singapore.</p>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="sticky top-32 space-y-6">
              <div className="rounded-[32px] border border-[#001935]/10 bg-[#001935] p-6 text-white shadow-[0_18px_60px_rgba(0,25,53,0.18)]" data-testid="news-article-insights-panel">
                <div className="flex items-center gap-3 text-[#F5D98C]"><BarChart3 className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.2em]">Market Insight</p></div>
                {article.key_stat ? <p className="mt-5 text-2xl font-semibold leading-tight">{article.key_stat}</p> : null}
                <div className="mt-6 space-y-4 text-sm leading-7 text-white/78">
                  {article.insight_points?.map((point, index) => <p data-testid={`news-article-insight-point-${index + 1}`} key={point}>• {point}</p>)}
                </div>
              </div>

              <div className="rounded-[32px] border border-[#D4AF37]/25 bg-[#fffaf1] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)]" data-testid="news-article-related-opportunities-card">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Matching opportunities</p>
                <h3 className="mt-4 text-2xl font-semibold text-[#001935]">Projects aligned to this market read.</h3>
                <div className="mt-6 space-y-4">
                  {(article.related_opportunities || []).map((listing, index) => (
                    <Link
                      className="block rounded-[24px] border border-[#001935]/10 bg-white px-4 py-4 transition hover:border-[#D4AF37]/50 hover:bg-[#fffdf7]"
                      data-testid={`news-article-related-opportunity-${index + 1}`}
                      key={listing.id}
                      to={buildBrandedPath(`/listings/${listing.id}`)}
                    >
                      <p className="text-sm font-semibold text-[#001935]">{listing.title}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{listing.district}</p>
                      <p className="mt-2 text-sm leading-6 text-[#42566f]">{formatListingPrice(listing)} • {listing.property_type}</p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-[32px] border border-[#D4AF37]/25 bg-white/90 p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)]" data-testid="news-article-takeaway-card">
                <div className="flex items-center gap-3 text-[#8b6d16]"><Sparkles className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.2em]">Key Takeaway</p></div>
                <p className="mt-4 text-2xl font-semibold leading-tight text-[#001935]">{article.takeaway}</p>
                <p className="mt-4 text-sm leading-7 text-[#42566f]">{article.advisory}</p>
                <div className="mt-6 grid gap-3">
                  <Link className="gold-button !w-full" data-testid="news-article-contact-link" to={buildBrandedPath("/contact")}>Speak with the Team</Link>
                  <Link className="outline-button !w-full !text-[#8b6d16]" data-testid="news-article-financials-link" to={buildBrandedPath("/financials")}>Review Financials</Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-[#F3EFE7] py-20" data-testid="news-related-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Continue Reading</p>
            <h2 className="mt-3 text-4xl font-semibold text-[#001935]">Related market context for your next decision.</h2>
            <p className="mt-4 text-base leading-8 text-[#42566f]">Move from one headline to the next with a more connected view of financing, district demand, and launch positioning.</p>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {article.related_articles?.map((relatedArticle, index) => <NewsEditorialCard article={relatedArticle} basePath="/insights" index={index} key={relatedArticle.id} />)}
          </div>
        </div>
      </section>
    </div>
  );
}